// React
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';

// Redux
import { useAppSelector } from '../../../redux/hooks';

// MUI Components
import { Autocomplete, Badge, Box, Button, Checkbox, Chip, CircularProgress, ClickAwayListener, Fade, FormControl, Icon, IconButton, Paper, Popper, PopperPlacementType, TextField, Tooltip, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// @MUI Icons
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

// Types
import { Job, Site } from '../../../@types';

// Utils
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { useTranslation } from 'react-i18next';
import { newAdapter } from "../../../utils";
import dayjs, { Dayjs } from 'dayjs';

// Services
import { getInterviewCountPassAndRejectGraphDashboardData } from '../../../services/DashboardService';
// import { getAllSites } from '../../../services/SiteService'; //เปลี่ยนมาใช้ useAppSelector
import { getAllJobs } from '../../../services/JobService';

// Custom Chart Color
const colorChart: { [key: string]: string[] } = {
    interviewColor: [
      '#007AFF',
      '#67C587',
      '#F94040',
    ],
};

const InterviewGraphDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    // เปลี่ยนมาใช้ useAppSelector
    // const [siteData, setSiteData] = useState<Site[]>([]);
    const { sites } = useAppSelector((state) => state.masterData);
    const siteData = useMemo(
        () => [...sites].sort((a: Site, b: Site) => a.site_id.localeCompare(b.site_id)),
        [sites]
    );
    const [jobsData, setJobsData] = useState<Job[]>([]);

    const [interviewGraphDashboardData, setInterviewDailyGraphDashboardData] = useState<any>(null);
    const [selectedSites, setSelectedSites] = useState<Site[]>([]);
    const [selectedJobs, setSelectedJobs] = useState<Job[]>([]);
    const [interviewDateRange, setInterviewDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loadingSites, setLoadingSites] = useState<boolean>(false); // เปลี่ยนมาใช้ useAppSelector (ข้อมูลพร้อมใช้จาก store แล้ว)
    const [loadingJobs, setLoadingJobs] = useState<boolean>(true);
    const [loadingInterviewDailyGraphDashboard, setLoadingInterviewDailyGraphDashboard] = useState<boolean>(true);
    const [countFilterInterviewDailyGraphDashboardSelected, setCountFilterInterviewDailyGraphDashboardSelected] = useState<number>(0);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [placement, setPlacement] = useState<PopperPlacementType>();
    const [filterInterviewDailyGraphDashboardOpen, setFilterInterviewDailyGraphDashboardOpen] = useState<boolean>(false);
    const [colorScheme, setColorScheme] = useState('interviewColor');

    const handleFilterInterviewDailyGraphDashboardOpenClick =
        (newPlacement: PopperPlacementType) =>
            (event: React.MouseEvent<HTMLButtonElement>) => {
                setAnchorEl(event.currentTarget);
                setFilterInterviewDailyGraphDashboardOpen((prev) => placement !== newPlacement || !prev);
                setPlacement(newPlacement);
            };

    useEffect(() => {
        const fetchInterviewDailyGraphDashboard = async () => {
            setLoadingInterviewDailyGraphDashboard(true);

            const response = await getInterviewCountPassAndRejectGraphDashboardData({
                site_ids: selectedSites?.map((site: Site) => site.site_id).length !== 0 ? selectedSites?.map((site: Site) => site.site_id) : undefined,
                interview_date_range: interviewDateRange[0] || interviewDateRange[1] ? interviewDateRange : undefined,
                job_ids: selectedJobs?.map((job: Job) => job.id).length !== 0 ? selectedJobs?.map((job: Job) => job.id) : undefined,
            });

            if (response.data?.success) {
                setLoadingInterviewDailyGraphDashboard(false);
                setInterviewDailyGraphDashboardData(response.data?.data);
            }
        };

        fetchInterviewDailyGraphDashboard();
    }, [selectedSites, interviewDateRange, selectedJobs]);

    // เปลี่ยนมาใช้ useAppSelector
    // useEffect(() => {
    //     const fetchSites = async () => {
    //         try {
    //             const response = await getAllSites();

    //             if (response.data?.success) {
    //                 setSiteData(response.data?.sites);
    //             }
    //         } catch (error) {
    //             navigate('/servererror', { replace: true });
    //         }
    //     };

    //     const siteSorting = async () => {
    //         try {
    //             siteData.sort((a, b) => a.site_id.localeCompare(b.site_id));
    //             setTimeout(() => {
    //                 setLoadingSites(false);
    //             }, 500);
    //         } catch (error) {
    //             navigate('/servererror', { replace: true });
    //         }
    //     }

    //     fetchSites();
    //     siteSorting();

    // }, []);

    useEffect(() => {
        const fetchJobs = async () => {
            setLoadingJobs(true);

            const response = await getAllJobs();

            if (response.data?.success) {
                setLoadingJobs(false);
                setJobsData(response.data?.jobs);
            }
        };

        fetchJobs();
    }, []);

    useEffect(() => {
        let count = 0;
        if (selectedSites && selectedSites.length > 0) count++;
        if (interviewDateRange[0] || interviewDateRange[1]) count++;
        if (selectedJobs && selectedJobs.length > 0) count++;
        setCountFilterInterviewDailyGraphDashboardSelected(count);
    }, [selectedSites, interviewDateRange, selectedJobs]);

    const handleClickAwayInterviewDailyGraphDashboardFilter = () => {
        setFilterInterviewDailyGraphDashboardOpen(false);
    };

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                    {t("The chart shows the numbers of interviews, passed and didn't pass interview")}
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { sm: 'fit-content(100%) 2fr' },
                    gridTemplateRows: { xs: 'fit-content(100%) repeat(1, 1fr)' },
                }}>
                    <Box sx={{ gridRow: { xs: 2, sm: 1 }, gridColumn: { sm: 3 } }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '50% 50%', sm: 'repeat(1, 1fr)' } }}>
                            <Box sx={{
                                py: 1, display: 'flex', alignItems: 'center', justifyContent: { sm: 'flex-end' }, '&:hover': {
                                    cursor: 'pointer',
                                }, gridColumn: 1
                            }}>
                                <Button onClick={handleFilterInterviewDailyGraphDashboardOpenClick('bottom-end')}>
                                    <Badge badgeContent={countFilterInterviewDailyGraphDashboardSelected} color="primary">
                                        <Icon >filter_list</Icon>
                                    </Badge>
                                    <Typography sx={{ userSelect: 'none' }}>
                                        {t("Filter")}
                                    </Typography>
                                </Button>

                                <Popper open={filterInterviewDailyGraphDashboardOpen} anchorEl={anchorEl} placement={placement} transition>
                                    {({ TransitionProps }) => (
                                        <ClickAwayListener onClickAway={handleClickAwayInterviewDailyGraphDashboardFilter}>
                                            <Fade {...TransitionProps} timeout={50}>
                                                <Paper sx={{ width: '400px', display: 'flex', flexDirection: 'column', py: 1, px: 3, border: '1px solid #e0e0e0' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <Box sx={{ width: '100%' }}>
                                                            <Typography sx={{ mt: 2, mb: 1, fontSize: '0.875rem' }}>{t("FilterBySite")}</Typography>
                                                            <FormControl sx={{ width: '100%' }}>
                                                                <Autocomplete
                                                                    onChange={(event, newValue: Site[]) => {
                                                                        setSelectedSites(newValue);
                                                                    }}
                                                                    multiple
                                                                    disableCloseOnSelect
                                                                    limitTags={1}
                                                                    id="site"
                                                                    options={
                                                                        siteData
                                                                    }
                                                                    size="small"
                                                                    loading={loadingSites}
                                                                    loadingText={`${t("Loading")}...`}
                                                                    getOptionLabel={(option) => option.site_name}
                                                                    value={selectedSites}
                                                                    renderOption={(props, option, { selected }) => (
                                                                        <li {...props}>
                                                                            <Checkbox
                                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                                style={{ marginRight: 8 }}
                                                                                checked={selected}
                                                                            />
                                                                            {option.site_name}
                                                                        </li>
                                                                    )}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params}
                                                                            label={loadingSites ? `${t("Loading")}...` : t("Site")}
                                                                            placeholder={t("Site")!}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                endAdornment: (
                                                                                    <React.Fragment>
                                                                                        {loadingSites ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                        {params.InputProps.endAdornment}
                                                                                    </React.Fragment>
                                                                                ),
                                                                            }} />
                                                                    )}
                                                                    renderTags={(value: any, getTagProps) =>
                                                                        value.map((option: Site, index: number) => (
                                                                            <Chip
                                                                                variant="outlined"
                                                                                label={t(option.site_name)}
                                                                                {...getTagProps({ index })}
                                                                            />
                                                                        ))
                                                                    }
                                                                    noOptionsText={t("No options found")}
                                                                />
                                                            </FormControl>
                                                        </Box>
                                                        <Tooltip title={t("Clear")}>
                                                            <IconButton
                                                                aria-label="delete"
                                                                size="small"
                                                                sx={{ height: 'fit-content', ml: 1 }}
                                                                onClick={() => {
                                                                    setSelectedSites([]);
                                                                }}
                                                            >
                                                                <Icon>clear</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <LocalizationProvider dateAdapter={newAdapter} adapterLocale={i18n.language === 'en' ? 'en' : 'th'}>
                                                            {/* MobileDateRangePicker (Pro) ถูกแทนด้วย DatePicker ฟรี 2 ตัว */}
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                                                    {t("FilterByInterviewDate")}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    <DatePicker
                                                                        label={t('StartDateRange')}
                                                                        value={interviewDateRange[0]}
                                                                        onChange={(date: any) => {
                                                                            setInterviewDateRange([
                                                                                date ? dayjs(date).startOf('day') : null,
                                                                                interviewDateRange[1],
                                                                            ]);
                                                                        }}
                                                                        format="DD/MM/YYYY"
                                                                        slotProps={{ textField: { size: 'small' } }}
                                                                    />
                                                                    <Typography>-</Typography>
                                                                    <DatePicker
                                                                        label={t('EndDateRange')}
                                                                        value={interviewDateRange[1]}
                                                                        minDate={interviewDateRange[0] ?? undefined}
                                                                        onChange={(date: any) => {
                                                                            setInterviewDateRange([
                                                                                interviewDateRange[0],
                                                                                date ? dayjs(date).endOf('day') : null,
                                                                            ]);
                                                                        }}
                                                                        format="DD/MM/YYYY"
                                                                        slotProps={{ textField: { size: 'small' } }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </LocalizationProvider>
                                                        <Tooltip title={t("Clear")}>
                                                            <IconButton
                                                                aria-label="delete"
                                                                size="small"
                                                                sx={{ height: 'fit-content', ml: 1 }}
                                                                onClick={() => {
                                                                    setInterviewDateRange([null, null]);
                                                                }}
                                                            >
                                                                <Icon>clear</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                                        <Box sx={{ width: '100%' }}>
                                                            <Typography sx={{ mt: 2, mb: 1, fontSize: '0.875rem' }}>{t("FilterByJob")}</Typography>
                                                            <FormControl sx={{ width: '100%' }}>
                                                                <Autocomplete
                                                                    onChange={(event, newValue: Job[]) => {
                                                                        setSelectedJobs(newValue);
                                                                    }}
                                                                    multiple
                                                                    disableCloseOnSelect
                                                                    limitTags={3}
                                                                    id="site"
                                                                    options={
                                                                        jobsData
                                                                    }
                                                                    size="small"
                                                                    loading={loadingJobs}
                                                                    loadingText={`${t("Loading")}...`}
                                                                    getOptionLabel={(option) => option.job_name}
                                                                    value={selectedJobs}
                                                                    renderOption={(props, option, { inputValue, selected }) => {
                                                                        const matches = match(option.job_name, inputValue, { insideWords: true });
                                                                        const parts = parse(option.job_name, matches);

                                                                        return (
                                                                            <li {...props}>
                                                                                <div>
                                                                                    {parts.map((part: any, index: any) => (
                                                                                        <span
                                                                                            key={index}
                                                                                            style={{
                                                                                                fontWeight: part.highlight ? 700 : 400,
                                                                                            }}
                                                                                        >
                                                                                            <Checkbox
                                                                                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                                                style={{ marginRight: 8 }}
                                                                                                checked={selected}
                                                                                            />
                                                                                            {part.text}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            </li>
                                                                        );
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params}
                                                                            label={loadingJobs ? `${t("Loading")}...` : t("Job position's name")}
                                                                            placeholder={t("Job position's name")!}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                endAdornment: (
                                                                                    <React.Fragment>
                                                                                        {loadingJobs ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                        {params.InputProps.endAdornment}
                                                                                    </React.Fragment>
                                                                                ),
                                                                            }} />
                                                                    )}
                                                                    renderTags={(value: any, getTagProps) =>
                                                                        value.map((option: Job, index: number) => (
                                                                            <Chip
                                                                                variant="outlined"
                                                                                label={t(option.job_name)}
                                                                                {...getTagProps({ index })}
                                                                            />
                                                                        ))
                                                                    }
                                                                    noOptionsText={t("No options found")}
                                                                />
                                                            </FormControl>
                                                        </Box>
                                                        <Tooltip title={t("Clear")}>
                                                            <IconButton
                                                                aria-label="delete"
                                                                size="small"
                                                                sx={{ height: 'fit-content', ml: 1 }}
                                                                onClick={() => {
                                                                    setSelectedJobs([]);
                                                                }}
                                                            >
                                                                <Icon>clear</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                                        <Button
                                                            sx={{
                                                                fontSize: 13,
                                                            }}
                                                            onClick={() => {
                                                                setSelectedSites([]);
                                                                setInterviewDateRange([null, null]);
                                                                setSelectedJobs([]);
                                                                setFilterInterviewDailyGraphDashboardOpen(false);
                                                            }}
                                                        >
                                                            {t('RemoveAllFilters')}
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Fade>
                                        </ClickAwayListener>
                                    )}
                                </Popper>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 5 }}>
                {
                    !loadingInterviewDailyGraphDashboard &&
                    <BarChart
                        width={900}
                        height={500}
                        series={[
                            { data: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.count_value_array, label: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.count_label, id: 'pvId' },
                            { data: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.pass_value_array, label: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.pass_label, id: 'uvId' },
                            { data: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.reject_value_array, label: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.reject_label, id: 'wvId' }
                        ]}
                        xAxis={[{ data: interviewGraphDashboardData?.interview_and_pass_of_each_site.display.xLabels, scaleType: 'band' }]}
                        slotProps={{
                            legend: {
                                direction: 'column',
                                position: { vertical: 'top', horizontal: 'middle' },
                            },
                        }}
                        colors={colorChart[colorScheme]}
                    />
                }
            </Box>
        </div>
    )
}

export default InterviewGraphDashboard